// Backward-compatible barrel — re-exports types from @/types and
// composes the legacy dataApi / adminApi objects from domain services.

// ──── Types (re-exported from @/types) ────
export type { PaginationParams, PaginationMeta, PaginatedResponse } from '@/types/common';
export type { Company, CompanyStats } from '@/types/company';
export type { Organization, CreateOrganizationInput } from '@/types/organization';
export type { Region, RegionStats, CreateRegionInput } from '@/types/region';
export type { Location } from '@/types/location';
export type { User, CreateUserInput, Role, PermissionGroup, CreateRoleInput, UpdateRoleInput } from '@/types/user';
export type { Material, MaterialRow, CreateMaterialInput } from '@/types/material';
export type { AddSubIngredientInput } from './services/material.api';
export type { UserRoleAssignment } from './services/user.api';
export type { AssignRoleScopeInput } from './services/user.api';
export type { Recipe } from '@/types/recipe';
export type { Menu } from '@/types/menu';

// ──── Domain services ────
import { companyApi } from './services/company.api';
import { organizationApi } from './services/organization.api';
import { locationApi } from './services/location.api';
import { userApi } from './services/user.api';
import { materialApi } from './services/material.api';
import { recipeApi } from './services/recipe.api';
import { recipeRequests } from './requests/recipes';
import { menuApi } from './services/menu.api';
import { roleApi } from './services/role.api';
import { regionApi } from './services/region.api';

export {
  companyApi,
  organizationApi,
  locationApi,
  userApi,
  materialApi,
  recipeApi,
  menuApi,
  roleApi,
  regionApi,
};

// ──── Legacy composite API (backward compat) ────
export const dataApi = {
  // Companies
  getCompanies: companyApi.getAll,
  paginateCompanies: companyApi.paginate,
  getCompany: companyApi.getById,
  createCompany: companyApi.create,
  updateCompany: companyApi.update,
  deleteCompany: companyApi.delete,

  // Company sub-resources
  getCompanyUsers: companyApi.getUsers,
  getCompanyMaterials: companyApi.getMaterials,
  getCompanyRecipes: companyApi.getRecipes,
  getCompanyMenus: companyApi.getMenus,
  getCompaniesStats: companyApi.getGlobalStats,

  // Organizations
  getOrganizations: organizationApi.getAll,
  paginateOrganizations: organizationApi.paginate,
  getOrganization: organizationApi.getById,
  getOrganizationCompanies: organizationApi.getCompanies,
  getOrganizationRegions: organizationApi.getRegions,
  createOrganization: organizationApi.create,
  updateOrganization: organizationApi.update,
  deleteOrganization: organizationApi.delete,
  getOrganizationsStats: (options?: { adminScope?: boolean }) => organizationApi.getGlobalStats(options),

  // Locations
  getLocations: locationApi.getAll,
  getLocation: locationApi.getById,
  createLocation: locationApi.create,
  updateLocation: locationApi.update,
  deleteLocation: locationApi.delete,

  // Users
  getUsers: userApi.getAll,
  paginateUsers: userApi.paginate,
  getUsersStats: userApi.getGlobalStats,
  getUser: userApi.getById,
  createUser: userApi.create,
  updateUser: userApi.update,
  assignUserRole: userApi.assignRole,
  deleteUser: userApi.delete,

  // Materials
  getMaterials: materialApi.getAll,
  getMaterial: materialApi.getById,
  createMaterial: materialApi.create,
  updateMaterial: materialApi.update,
  deleteMaterial: materialApi.delete,
  getMaterialUsage: materialApi.getUsage,
  addSubIngredient: materialApi.addSubIngredient,
  removeSubIngredient: materialApi.removeSubIngredient,

  // Recipes
  getRecipes: recipeRequests.list,
  getRecipe: recipeRequests.getById,
  createRecipe: recipeRequests.create,
  updateRecipe: recipeRequests.update,
  deleteRecipe: recipeRequests.delete,

  // Menus
  getMenus: menuApi.getAll,
  getMenu: menuApi.getById,
  createMenu: menuApi.create,
  updateMenu: menuApi.update,
  deleteMenu: menuApi.delete,

  // Roles
  getRoles: (params?: { organizationId?: number }) => roleApi.getAll(params),
  getRole: roleApi.getById,
  getSystemRoles: roleApi.getSystemRoles,
  getRolePermissions: roleApi.getPermissions,
  createRole: roleApi.create,
  updateRole: roleApi.update,
  deleteRole: roleApi.delete,
};

export const adminApi = dataApi;

